import PluginBase from './base';
import Logger from '../lib/logger';
import ScriptLoader from '../lib/script_loader';

const DEFAULT_SPLITTER_READY_TIMEOUT_MS = 3000;
const SPLITTER_READY_POLL_INTERVAL_MS = 50;

export default class OptimeraCommon extends PluginBase {
  constructor({ app }) {
    super({ app });
    this.isInitialized = false;
    this.slotsInserted = false;
    this.slots = [];
    this.slotIds = [];
    /** Splitter scores keyed by slot div id */
    this.optimeraSplitterScores = {};
    /** True once the hardcoded page-data script has loaded */
    this.pageDataLoaded = false;
    /** Guards against fetching the per-page scorefile more than once, now that
     *  the fetch is kicked off early in onSettingsLoaded. */
    this.scorefileFetchStarted = false;
    /** Guards against loading the IVT (oHS) script more than once */
    this.ivtLoaded = false;
    /** Prevents overlapping wait loops for targeting readiness */
    this.waitingForTargetingReady = false;
  }

  onSettingsLoaded() {
    Logger.log('Installing plugin: Optimera');
    this.setUpOptimera();
    // Load the IVT detection script as early as possible and don't wait for it,
    // mirroring Atlas which fires oHS on Config.ready. Gated by config.
    this.loadIvtScript();
    // Start the per-page scorefile fetch as early as possible, in parallel with
    // the common library, so window.oVa is populated before slots are requested.
    // The scorefile URL depends only on host + path, not on the slot list, so it
    // does not need to wait for slotsInserted. Fetching it here (instead of on
    // slotsInserted) avoids the race where scores arrived after the slot was
    // published to the ad server and no slots received the `optimera` key.
    this.loadOptimeraAdTargetingScript();
  }

  // For Hardcoded implementation we need to determine which slots are on the
  // page. The scorefile fetch was already kicked off in onSettingsLoaded; here
  // we build the slot list and apply targeting (scores may already be ready).
  //
  // The batch prep (slot list, per-slot NULL pre-set, page-level opti_page, and
  // populating the splitter scores) always runs at slotsInserted, mirroring
  // Atlas which pre-sets NULL up front and fills scores when the scorefile lands.
  // What settings.optimera.activateOnEvent controls is which event (re)applies
  // the real `optimera` targeting: 'slotsInserted' (default, here),
  // 'bidManagerCreated', or 'slotsFinalized' (page-triggered via
  // app.finalizeSlots(), after all slots are registered).
  onSlotsInserted() {
    Logger.log('Setting up Optimera Hardcoded Ad Targeting');
    this.setUpOptimeraAdTargeting();
  }

  // Alternative activation point for the per-slot apply. The batch prep already
  // ran at slotsInserted; when this is the selected event we (re)apply the
  // splitter-score targeting here. Selected via activateOnEvent =
  // 'bidManagerCreated'. (Note: bidManagerCreated actually fires BEFORE the
  // insert loop, so scores are applied as they arrive via the fetch onload.)
  onBidManagerCreated() {
    if (!this.shouldActivateOnEvent('bidManagerCreated')) return;
    Logger.log('Setting up Optimera Hardcoded Ad Targeting (bidManagerCreated)');
    this.setUpOptimeraAdTargeting();
  }

  /**
   * Latest activation point: fired once from the page (via app.finalizeSlots()
   * — see concert-ads display_control mixin, called from pmc-adm-v2 concert-footer.php)
   * after every slot has been registered but before the ad requests fire. This
   * is the closest Concert equivalent to Atlas running Optimera as a pre-auction
   * provider. Selected via settings.optimera.activateOnEvent = 'slotsFinalized'.
   *
   * The scorefile fetch was kicked off in onSettingsLoaded and the batch prep
   * (slot list, NULL pre-set, page-level opti_page) ran at slotsInserted, so here
   * we (re)apply the splitter-score targeting across all slots now that the full
   * slot list is known — mirroring Atlas's post-load loop that stamps
   * setTargeting('optimera', w.oVa[domId] ?? 'NULL') on every slot.
   */
  onSlotsFinalized() {
    if (!this.shouldActivateOnEvent('slotsFinalized')) return;
    if (!this.isEligibleForHardcodedTargeting()) return;
    Logger.log('Setting up Optimera Hardcoded Ad Targeting (slotsFinalized)');
    this.setUpOptimeraAdTargeting();
  }

  /**
   * Whether hardcoded ad targeting should (re)apply on the given lifecycle event.
   * Config-driven so we can move the trigger without a code change. The value of
   * settings.optimera.activateOnEvent maps to a real concert-ads event emitted
   * before (or at) the ad-server publish:
   *   'slotsInserted'      (default) — slots known, earliest safe batch point
   *   'bidManagerCreated'  — before the insert loop; batch
   *   'slotsFinalized'     — page-triggered via app.finalizeSlots(), after every
   *                          slot is registered but before the ad requests fire
   * Any unset/unrecognized value falls back to 'slotsInserted'.
   * @param {string} eventName
   * @returns {boolean}
   */
  shouldActivateOnEvent(eventName) {
    const value = this.app.settings.optimera?.activateOnEvent;
    const active = value === 'bidManagerCreated' || value === 'slotsFinalized' ? value : 'slotsInserted';
    return active === eventName;
  }

  /**
   * Check if we want to load Optimera
   */
  isEligible() {
    const { enabled, scriptUrl } = this.app.settings.optimera?.common ?? {};
    return Boolean(enabled) && typeof scriptUrl === 'string' && scriptUrl.length > 0;
  }

  /**
   * Check if we want to load Optimera Hardcoded Targeting script
   */
  isEligibleForHardcodedTargeting() {
    const { enabled, dataHost, clientId } = this.app.settings.optimera?.hardcodedTargeting ?? {};
    return Boolean(enabled) && typeof dataHost === 'string' && typeof clientId === 'number';
  }

  /**
   * Check if we want to load the Optimera IVT (oHS) script.
   */
  isEligibleForIvt() {
    const { enabled, scriptUrl } = this.app.settings.optimera?.ivt ?? {};
    return Boolean(enabled) && typeof scriptUrl === 'string' && scriptUrl.length > 0;
  }

  /**
   * The custom-param key names Optimera should track, from config. Mirrors
   * Atlas's $$optimera_kvps_list$$ global setting, migrated to conductor-configs
   * at optimera.hardcodedTargeting.kvpsList.
   * @returns {string[]}
   */
  getKvpsList() {
    const list = this.app.settings.optimera?.hardcodedTargeting?.kvpsList;
    return Array.isArray(list) ? list.filter(k => typeof k === 'string') : [];
  }

  /**
   * Set up Optimera
   */
  async setUpOptimera() {
    Logger.log('Setting up Optimera');

    if (this.isInitialized) {
      Logger.log('Optimera already initialized, skipping setup');
      return;
    }

    if (!this.isEligible()) {
      Logger.log('Optimera not eligible, skipping setup');
      return;
    }

    try {
      await this.loadOptimeraScript();
      const initialized = await this.ensureOptimeraInitialized();
      if (!initialized) {
        Logger.error('Failed to initialize Optimera: optiCommon.init never became available');
        return;
      }

      Logger.log('Optimera Common setup completed successfully');

      // Page-data file may have finished loading first; apply targeting now that init ran.
      if (this.pageDataLoaded) {
        this.getSplitterScoresAndApplyTargeting();
      }
    } catch (error) {
      Logger.error(`Failed to set up Optimera: ${error}`);
    }
  }

  /**
   * Load the Optimera script
   */
  async loadOptimeraScript() {
    const dependency = {
      timeout: this.app.settings.optimera?.common?.scriptTimeout ?? 3000,
      url: this.app.settings.optimera?.common?.scriptUrl,
    };

    try {
      // Bail if we are loading the script in the CMS to help targeting performance
      if (document.querySelector(`script[src="${dependency.url}"]`)) {
        Logger.log(`Optimera script ${dependency.url} is already loaded on the page`);
        return;
      }

      const url = await new ScriptLoader().load(dependency);
      Logger.log(`Optimera script ${url} has loaded successfully`);
    } catch (error) {
      Logger.error(`Error loading Optimera script from ${this.app.settings.optimera?.common?.scriptUrl}: ${error}`);
    }
  }

  /**
   * Ensure optiCommon.init has been called with configured settings.
   * Retries when the script tag is present but the library has not finished executing.
   * @returns {Promise<boolean>}
   */
  async ensureOptimeraInitialized() {
    if (this.isInitialized) return true;

    if (this.initOptimera()) {
      this.isInitialized = true;
      return true;
    }

    return this.waitForOptiCommonInit();
  }

  /**
   * Whether window.optiCommon exposes init.
   * @returns {boolean}
   */
  isOptiCommonInitApiReady() {
    return typeof window !== 'undefined' && !!window.optiCommon && typeof window.optiCommon.init === 'function';
  }

  /**
   * Poll until optiCommon.init is available, then call it with config.
   * @returns {Promise<boolean>}
   */
  waitForOptiCommonInit() {
    const timeoutMs = this.getCommonReadyTimeoutMs();
    const startedAt = Date.now();

    Logger.log(`Optimera: waiting up to ${timeoutMs}ms for optiCommon.init`);

    return new Promise(resolve => {
      const poll = () => {
        if (this.isOptiCommonInitApiReady() && this.initOptimera()) {
          this.isInitialized = true;
          resolve(true);
          return;
        }

        if (Date.now() - startedAt >= timeoutMs) {
          Logger.warn('Optimera: optiCommon.init unavailable after waiting');
          resolve(false);
          return;
        }

        setTimeout(poll, SPLITTER_READY_POLL_INTERVAL_MS);
      };

      setTimeout(poll, SPLITTER_READY_POLL_INTERVAL_MS);
    });
  }

  /**
   * Initialize Optimera Common with default settings.
   * @returns {boolean} Whether init ran successfully
   */
  initOptimera() {
    if (typeof window === 'undefined') {
      Logger.warn('Optimera init skipped (SSR environment detected)');
      return false;
    }
    const oc = window.optiCommon;
    if (!oc || typeof oc.init !== 'function') {
      Logger.warn('Optimera script loaded but window.optiCommon.init is not available yet');
      return false;
    }
    // KVPs to forward as Optimera custom params, migrated from Atlas's
    // $$optimera_kvps_list$$ global setting into conductor-configs.
    const kvpsList = this.getKvpsList();

    const defaultConfig = {
      splitters: {
        additionalOptimeraParams: [],
        // Mirror Atlas: register the KVP keys as customParams so Optimera tracks
        // them. Atlas also sent 'loggedIn' and 'country_code'; those are deferred
        // for this pass (no concert-ads source yet).
        customParams: [...kvpsList],
        // Atlas ignored Optimera's 'os'/'browser' defaults; keep that unless
        // overridden by config.
        ignoreOptimeraDefaults: ['os', 'browser'],
      },
    };
    // Optional shallow overrides from settings; deep-merge if nested overrides are needed.
    const overrides = this.app.settings.optimera?.common?.config || {};
    const config = { ...defaultConfig, ...overrides };
    oc.init(config);

    // Forward the current value of each KVP to Optimera, reading concert's own
    // page-level variables (the closest equivalent to Atlas GlobalTargeting).
    this.sendCustomParams(oc, kvpsList);
    return true;
  }

  /**
   * Forward each configured KVP's current page-variable value to Optimera via
   * setCustomParam. Mirrors Atlas's loop over $$optimera_kvps_list$$.
   *
   * @param {object} oc - window.optiCommon
   * @param {string[]} kvpsList
   */
  sendCustomParams(oc, kvpsList) {
    if (!oc || typeof oc.setCustomParam !== 'function' || !kvpsList.length) return;
    let allVariables = {};
    try {
      allVariables = this.app.getAllVariables?.() ?? {};
    } catch (error) {
      Logger.warn(`Optimera: could not read page variables for custom params: ${error}`);
      return;
    }
    kvpsList.forEach(name => {
      const raw = allVariables[name];
      if (raw === undefined || raw === null) return;
      const values = Array.isArray(raw) ? raw : [raw];
      values.forEach(value => oc.setCustomParam({ name, value: value.toString() }));
    });
  }

  /**
   * Set up Optimera Hardcoded Ad Targeting
   */
  setUpOptimeraAdTargeting() {
    if (!this.isEligibleForHardcodedTargeting()) return;
    Logger.log(`Setting up hardcoded targeting for Optimera`);

    this.slots = this.app.slots.all();
    this.slotIds = this.slots.map(slot => slot.id);
    // Register every slot's div id with Optimera (window.oDv), the div list
    // Optimera uses for slot-level tracking. Atlas parity — code provided by
    // Optimera: oDv[0] is the client id, the rest are the tracked slot ids.
    this.registerSlotsWithOptimera();
    // Pre-set the default per-slot targeting to 'NULL' (Atlas parity): this
    // guarantees every slot carries the `optimera` key even if the scorefile is
    // slow or fails, and the real value overwrites it once scores arrive. This
    // is what prevents the "0 slots keyed" race when scores land after request.
    const key = this.gamTargetingKey();
    this.slotIds.forEach(slotId => {
      this.optimeraSplitterScores[slotId] = ['NULL'];
      const slot = this.slots.find(s => s.id === slotId);
      if (slot && typeof slot.setTargeting === 'function' && slot.getTargeting?.(key) == null) {
        slot.setTargeting(key, 'NULL');
      }
    });

    // Default the page-level key to 'fail' before the async fetch resolves, so a
    // slow/failed scorefile still leaves a value (Atlas sets opti_page='fail'
    // up front). Overwritten with oSF.pagelevel once page data is available.
    this.applyPageLevelTargeting();

    // The scorefile fetch was started in onSettingsLoaded. If it already
    // resolved, apply targeting now that the slots are known; otherwise the
    // fetch's onload handler will call getSplitterScoresAndApplyTargeting once
    // the page data is available.
    if (this.pageDataLoaded) {
      this.getSplitterScoresAndApplyTargeting();
    }
  }

  /**
   * Register each slot's div id with Optimera's div list, window.oDv — the code
   * Optimera provided for slot-level targeting (Atlas Optimera.js L74-86). oDv[0]
   * holds the client id; every slot div id is pushed once so Optimera tracks it,
   * even before the scorefile has data for that slot. Idempotent per div id.
   */
  registerSlotsWithOptimera() {
    const { clientId } = this.app.settings.optimera?.hardcodedTargeting ?? {};
    window.oDv = window.oDv || [];
    window.oDv[0] = String(clientId);
    this.slotIds.forEach(slotId => {
      if (!window.oDv.includes(slotId)) {
        window.oDv.push(slotId);
      }
    });
  }

  /**
   * The per-slot GAM key for Optimera splitter values. Configurable via
   * settings.optimera.hardcodedTargeting.gamTargetingKey (default 'optimera').
   * @returns {string}
   */
  gamTargetingKey() {
    return this.app.settings.optimera?.hardcodedTargeting?.gamTargetingKey ?? 'optimera';
  }

  /**
   * The page-level GAM key for Optimera's page-level score. Configurable via
   * settings.optimera.hardcodedTargeting.pageTargetingKey (default 'opti_page').
   * @returns {string}
   */
  pageTargetingKey() {
    return this.app.settings.optimera?.hardcodedTargeting?.pageTargetingKey ?? 'opti_page';
  }

  /**
   * Set the page-level `opti_page` GAM key from window.oSF.pagelevel (the value
   * the scorefile publishes for the whole page). Mirrors Atlas Optimera.js:
   * default 'fail' before/without data, real array once oSF.pagelevel exists.
   * Uses this.app.addVariable, which routes to googletag.pubads().setTargeting.
   */
  applyPageLevelTargeting() {
    const key = this.pageTargetingKey();
    const pageLevel = window.oSF?.pagelevel;
    const value = Array.isArray(pageLevel) && pageLevel.length > 0 ? pageLevel : 'fail';
    this.app.addVariable(key, value);
  }

  /**
   * Load the Optimera IVT (oHS) detection script as early as possible; do not
   * wait for it. Gated by settings.optimera.ivt (enabled + scriptUrl).
   * Mirrors Atlas Optimera.js loading oHS-21.js on Config.ready.
   */
  loadIvtScript() {
    if (this.ivtLoaded || !this.isEligibleForIvt()) return;
    this.ivtLoaded = true;
    const { scriptUrl, scriptTimeout } = this.app.settings.optimera.ivt;
    const dependency = { url: scriptUrl, timeout: scriptTimeout ?? 3000 };
    new ScriptLoader()
      .load(dependency)
      .then(url => Logger.log(`Optimera IVT script ${url} has loaded`))
      .catch(error => Logger.error(`Error loading Optimera IVT script from ${scriptUrl}: ${error}`));
  }

  /**
   * Load Optimera Hardcoded Ad Targeting Script
   */
  loadOptimeraAdTargetingScript() {
    if (typeof window === 'undefined') return;
    // Called early from onSettingsLoaded: only fetch once, and bail when
    // hardcoded targeting is not configured. isEligibleForHardcodedTargeting()
    // guarantees the hardcodedTargeting block (and its dataHost/clientId) exists,
    // so the destructure below is safe.
    if (this.scorefileFetchStarted || !this.isEligibleForHardcodedTargeting()) return;
    this.scorefileFetchStarted = true;
    try {
      const { dataHost, clientId } = this.app.settings.optimera.hardcodedTargeting;
      const pageHost = window.location.host;
      const pagePath = window.location.pathname;
      const optimeraTargetingScript = document.createElement('script');
      optimeraTargetingScript.async = true;
      optimeraTargetingScript.type = 'text/javascript';
      optimeraTargetingScript.onload = () => {
        this.pageDataLoaded = true;
        this.getSplitterScoresAndApplyTargeting();
      };
      const src = dataHost + '/' + clientId + '/' + pageHost + pagePath + '.js';
      optimeraTargetingScript.src = src;
      const node = document.getElementsByTagName('script')[0];
      node.parentNode.insertBefore(optimeraTargetingScript, node);
      Logger.log(`Loading Optimera ad targeting script ${src}`);
    } catch (error) {
      Logger.error(`There was an error loading the Optimera ad targeting script: ${error}`);
    }
  }

  /**
   * Whether window.optiCommon exposes getSplitterScores.
   * @returns {boolean}
   */
  isSplitterScoresApiReady() {
    return (
      typeof window !== 'undefined' && !!window.optiCommon && typeof window.optiCommon.getSplitterScores === 'function'
    );
  }

  /**
   * Targeting requires successful optiCommon.init (config applied) and getSplitterScores.
   * @returns {boolean}
   */
  isReadyToApplyTargeting() {
    return this.isInitialized && this.isSplitterScoresApiReady();
  }

  /**
   * How long to wait for the common library / splitter API.
   * @returns {number}
   */
  getCommonReadyTimeoutMs() {
    return this.app.settings.optimera?.common?.scriptTimeout ?? DEFAULT_SPLITTER_READY_TIMEOUT_MS;
  }

  /**
   * How long to wait for targeting readiness after page data loads.
   * @returns {number}
   */
  getSplitterReadyTimeoutMs() {
    return this.app.settings.optimera?.hardcodedTargeting?.splitterReadyTimeout ?? this.getCommonReadyTimeoutMs();
  }

  /**
   * Get ad targeting data (splitter scores) and apply.
   * Waits until optiCommon has been initialized with config and getSplitterScores exists —
   * page data and the common library load independently, so either can finish first.
   */
  getSplitterScoresAndApplyTargeting() {
    if (typeof window === 'undefined') return;
    if (!this.pageDataLoaded) return;

    if (!this.isReadyToApplyTargeting()) {
      this.waitForTargetingReadyThenApply();
      return;
    }

    this.waitingForTargetingReady = false;

    try {
      const scores = window.optiCommon.getSplitterScores();
      if (scores && typeof scores === 'object') {
        Logger.log(`Applying splitter score targeting for Optimera`);
        this.optimeraSplitterScores = scores;
        this.slotIds.forEach(slotId => this.applyOptimeraGamTargetingToSlot(slotId));
      }
      // Page-level score comes from the scorefile's oSF.pagelevel, which is now
      // populated. Overwrites the 'fail' default set before the fetch.
      this.applyPageLevelTargeting();
    } catch (error) {
      Logger.error(`There was an error applying Optimera targeting: ${error}`);
    }
  }

  /**
   * Poll until init has run and getSplitterScores exists (or timeout), then apply targeting.
   */
  waitForTargetingReadyThenApply() {
    if (this.waitingForTargetingReady) return;

    this.waitingForTargetingReady = true;
    const timeoutMs = this.getSplitterReadyTimeoutMs();
    const startedAt = Date.now();

    Logger.log(`Optimera: waiting up to ${timeoutMs}ms for optiCommon.init and getSplitterScores`);

    const poll = () => {
      if (this.isReadyToApplyTargeting()) {
        this.waitingForTargetingReady = false;
        this.getSplitterScoresAndApplyTargeting();
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        this.waitingForTargetingReady = false;
        Logger.warn('Optimera: init/getSplitterScores unavailable after waiting; GAM targeting not applied');
        return;
      }

      setTimeout(poll, SPLITTER_READY_POLL_INTERVAL_MS);
    };

    setTimeout(poll, SPLITTER_READY_POLL_INTERVAL_MS);
  }

  /**
   * Splitter value for a slot DOM id (same id used in oDv / Optimera div list).
   *
   * @param {string} slotId
   * @returns {unknown|undefined}
   */
  getOptimeraValueForSlotId(slotId) {
    return this.optimeraSplitterScores[slotId];
  }

  /**
   * Applies the Optimera splitter value for this slot id as slot-level GAM targeting.
   * Configure the key in settings.optimera.hardcodedTargeting.gamTargetingKey (default optimera).
   *
   * @param {string} slotId - DOM id for the slot (oDv entry)
   * @param {object} slots
   * @returns {boolean} Whether targeting was applied
   */
  applyOptimeraGamTargetingToSlot(slotId) {
    const key = this.gamTargetingKey();
    const slot = this.slots.find(s => s.id === slotId);
    if (!slot || typeof slot.setTargeting !== 'function') {
      Logger.warn(`Optimera: no GAM slot with setTargeting found for id ${slotId}`);
      return false;
    }
    const value = this.getOptimeraValueForSlotId(slotId);
    if (value === undefined) {
      Logger.warn(`Optimera: no splitter value for slot id ${slotId}`);
      return false;
    }
    slot.setTargeting(key, value);
    return true;
  }
}
